import Vue from 'vue'
import {
  assembleTravelChargeUrl,
  dateReceived,
  getParentEntityIdProjectDepartmentJobHash,
  nextBusinessDay,
  objectById,
  priorityLabel
} from 'src/utilities/utils'
import {ptracDeleteEntity, ptracGet, ptracGetEntities} from 'src/utilities/ptracApi'
import {crudifyMutations, hydrateEdit} from 'src/utilities/vuex2'
import {getCompanies, getPlanNames} from 'src/utilities/ptracApiHelper'
import Constants from 'src/utilities/constants'
import search from './search'
import {BAD_REQUEST, OK} from 'http-status-codes'
import {make} from 'vuex-pathify'
import _ from 'lodash'

const create = {
  jobTemplateId: null,
  projectId: null,
  departmentId: null,
  processDefinitionId: null,
  processDefinitionKey: null,
  number: null,
  permitNumber: null,
  name: null,
  coordinatorId: null,
  priority: Constants.PRIORITIES.MEDIUM.VALUE,
  status: Constants.STATUSES.PENDING,
  clientId: null,
  clientContactId: null,
  builderId: null,
  builderContactId: null,
  community: {},
  dateReceived: dateReceived(),
  targetCompletionDate: nextBusinessDay(),
  description: '',
  planNameId: null,
  elevation: null,
}

const edit = {
  id: null,
  jobTemplateId: null,
  projectId: null,
  departmentId: null,
  processDefinitionId: null,
  processDefinitionKey: null,
  processInstanceId: null,
  number: null,
  permitNumber: null,
  name: null,
  coordinatorId: null,
  priority: Constants.PRIORITIES.MEDIUM.VALUE,
  status: Constants.STATUSES.PENDING,
  clientId: null,
  clientContactId: null,
  builderId: null,
  builderContactId: null,
  community: {},
  dateReceived: null,
  targetCompletionDate: null,
  actualCompletionDate: null,
  invoiceBy: null,
  description: '',
  planNameId: null,
  elevation: null,
  createdBy: null,
  updatedBy: null,
  createdAt: null,
  updatedAt: null
}

const remember = {
  client: false,
  clientContact: false,
  builder: false,
  builderContact: false,
  community: false,
  targetCompletionDate: false
}

const remembered = {
  client: null,
  clientContact: null,
  builder: null,
  builderContact: null,
  community: null,
  targetCompletionDate: null
}

const initialState = {
  create,
  edit,
  remember,
  remembered,
  updateTaskDueDates: false,
  projectJobs: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)

getters.savePayload = (state) => {
  let data = state.create
  if (state.edit.id) {
    data = state.edit
  }
  const payload = JSON.parse(JSON.stringify({
    endpoint: 'jobs',
    data: data
  }))
  if (state.edit.id) {
    payload.id = state.edit.id
  }
  return payload
}

getters.byDepartment = (state) => {
  return departmentId => state.projectJobs.filter(job => {
    return job.departmentId === departmentId
  })
}
getters.byDepartmentId = (state) => (departmentId) => {
  return state.projectJobs.filter(job => {
    return job.departmentId === departmentId
  })
}

getters.byId = (state) => (id) => {
  return objectById(state.projectJobs, id)
}

const mutations = make.mutations(state)

mutations.hydrateEdit = hydrateEdit
crudifyMutations(mutations)
mutations.replaceJob = (state, newJob) => {
  const job = objectById(state.projectJobs, newJob.id)
  for (let prop in newJob) {
    if (prop !== 'id') {
      job[prop] = newJob[prop]
    }
  }
}

mutations.resetEdit = (state) => {
  Object.keys(initialState.edit).forEach(key => {
    state.edit[key] = null
  })

  state.edit.targetCompletionDate = nextBusinessDay()
  state.edit.priority = Constants.PRIORITIES.MEDIUM.VALUE
  state.edit.status = Constants.STATUSES.PENDING

  delete state.edit.folderName
  delete state.edit.invoiceByTask
  delete state.edit.fromUserId
  delete state.edit.dateReceived
  delete state.edit.route
  delete state.edit.updatedAt
  delete state.edit.updatedBy
  delete state.edit.entityType
}

mutations.resetCreate = (state) => {
  Object.keys(initialState.create).forEach(key => {
    state.create[key] = null
  })
  state.create.dateReceived = dateReceived()
  state.create.targetCompletionDate = nextBusinessDay()
  state.create.priority = Constants.PRIORITIES.MEDIUM.VALUE
  state.create.status = Constants.STATUSES.PENDING
}

mutations.partialResetCreate = (state) => {
  state.create.jobTemplateId = null
  state.create.elevation = null
  state.create.number = null
  state.create.permitNumber = null
  state.create.dateReceived = dateReceived()
  state.create.targetCompletionDate = nextBusinessDay()
  state.create.priority = Constants.PRIORITIES.MEDIUM.VALUE
  state.create.status = Constants.STATUSES.PENDING
}

mutations.resetProjectJobs = (state) => {
  state.projectJobs = []
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchFindByBuilderIdOrClientId({}, companyId) {
      return await ptracGetEntities('jobs/search/findByBuilderIdOrClientId?clientId=' + companyId + '&builderId=' + companyId)
    },
    async fetchExistsByCommunityId({}, communityId) {
      return await ptracGetEntities('jobs/search/existsByCommunity_Id?communityId=' + communityId)
    },
    async fetchByProjectId({state, commit, dispatch, rootGetters}, projectId) {
      const isSuperUser = rootGetters['security/isInGroup'](Constants.GROUPS.SUPER_USERS)

      const response = await ptracGetEntities('jobs/search/findByProjectIdOrderByCreatedAtDesc?projectId=' + projectId)

      if (OK === response.status) {
        const jobs = response.data
        const companies = await getCompanies(jobs, 'clientId', 'builderId')
        const planNames = await getPlanNames(jobs, 'planNameId')

        for (let job of jobs) {
          if (companies.has(job.clientId)) {
            job.client = companies.get(job.clientId)
          }

          if (companies.has(job.builderId)) {
            job.builder = companies.get(job.builderId)
          }

          if (job.client && job.clientContactId) {
            job.clientContact = objectById(job.client.contacts, job.clientContactId)
          }

          if (job.builder && job.builderContactId) {
            job.builderContact = objectById(job.builder.contacts, job.builderContactId)
          }

          if (job.planNameId) {
            job.planName = objectById(planNames, job.planNameId)
          }

          if (job.priority) {
            job.priorityLabel = priorityLabel(job.priority)
            job.priority = +job.priority // Coerced to number to allow column sorting
          }

          if (isSuperUser && job.status !== Constants.STATUSES.COMPLETED && job.status !== Constants.STATUSES.CANCELLED) {
            const incidentsResponse = await ptracGet(`camunda/incident?processInstanceId=${job.processInstanceId}`)

            if (OK === incidentsResponse.status && incidentsResponse.data.length > 0) {
              job.camundaIncidents = incidentsResponse.data
            }
          }

          commit('comments/addIdHash', getParentEntityIdProjectDepartmentJobHash(job.projectId, job.departmentId, job.id), {root: true})
        }

        state.projectJobs = jobs
        const jobIds = _.map(state.projectJobs, 'id')
        if (jobIds) {
          dispatch('logs/fetchByEntityIds', jobIds, {root: true})
        }
        this.$eventHub.$emit('projectJobsLoaded')
      }

      return response
    },
    async fetchByProjectIdAndDepartmentId({}, payload) {
      return await ptracGetEntities('jobs/search/findByProjectIdAndDepartmentIdOrderByCreatedAtDesc?projectId=' + payload.projectId + '&departmentId=' + payload.departmentId)
    },
    async fetchJobNumberExists({state}) {
      return await ptracGetEntities('jobs/search/existsByNumberAndIdNot?number=' + state.number + '&id=' + state.id)
    },
    async fetchByIdAndReplace({commit}, id) {
      const response = await ptracGetEntities('jobs/' + id)
      if (OK === response.status) {
        commit('replaceJob', response.data)
      }
    },
    async fetchJobNumberSequenceAndReturn() {
      return await ptracGet(`sequences/next?entityType=${Constants.ENTITY_TYPES.JOB}&datePrefix=yyyy-MM--`)
    },
    async fetchTravelCharge({state}, payload) {
      if (payload && payload.name) {
        const url = assembleTravelChargeUrl(payload)
        if (!url) {
          return { status: BAD_REQUEST }
        }
        return await ptracGet(url)
      }

      let job = state.create

      if (state.edit.id) {
        job = state.edit
      }

      const url = assembleTravelChargeUrl(job)

      if (!url) {
        return { status: BAD_REQUEST }
      }

      return await ptracGet(url)
    },
    async terminate({}, jobIds) {
      return await ptracDeleteEntity(`jobs/terminate?jobIds=${jobIds}`)
    }
  },
  modules: {
    search
  }
}
